import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useSearchParams, useNavigate } from "react-router-dom";
import { PiMapPinLight } from "react-icons/pi";
import CustomLoader from "../../../../controls/CustomLoader";
import {
  getCompanyMarketAppAccountIdByCompanyId,
  createRyftSession,
  getAllServicesByCompanyId, 
  getCompanyDetails, 
  setCurrentCompanyId,
  getPharmacyByService,
  createPaymentAudit,
  getPatientConsultation,
  getPaymentViaLinkDetails
} from "../../../../../dispatchers/bookingDispatchers";
import { saveService } from "../../../../../dispatchers/templateSectionDispatchers";
import { showSuccess, showError } from "../../../../ToastedMessage/ToastedMessage";
import "../../../../../Assets/css/BookingPage.scss";
import Lottie from "lottie-react";
import successAnimation from "./../../../../../Assets/success.json";

const fetchCompanyDetails = async (companyId, serviceId) => {
    const res = await getCompanyDetails({
                companyID: companyId,
                serviceID: serviceId,
            });
    setCurrentCompanyId(companyId);
    return res?.companyDetails;
};

const getCompanyServices = async (companyId) => {
  const res = await getAllServicesByCompanyId({ companyID: companyId });
  return res;
};

const getBranchDetails = async (companyId, serviceId, branchId) => {
    const res = await getPharmacyByService({
                    companyID: companyId,
                    serviceID: serviceId,
                });
    const branches = res?.data || res;

    const selectedBranch = branches.find(
        (branch) => branch.pharmacyID === branchId
    );

    if (!selectedBranch) {
        throw new Error("Branch not found");
    }

    return selectedBranch;
};

const fetchPatientConsultation = async (consultationID) => {
  const res = await getPatientConsultation({ consultationID: consultationID });
  return res;
};

export default function AppointmentPaymentDetailsViaLink({ mainColor = "#0A5FDC" }) {
  const formRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [clientSecret, setClientSecret] = useState(null);
  const [isCardValid, setIsCardValid] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [accountId, setAccountId] = useState(null);
  const [paymentSuccessful, setPaymentSuccessful] = useState(false);

  const [bookingState, setBookingState] = useState(null);
  const animRef = useRef(null);
  
  useEffect(() => {
  if (paymentSuccessful) {
    const timeout = setTimeout(() => {
      animRef?.current?.play();
    }, 500);

    return () => clearTimeout(timeout);
  }
  }, [paymentSuccessful]);

  useEffect(() => {
    const loadBookingState = async () => {
      try {
        const consultationId = searchParams.get("consultationId");

        if (!consultationId) {
        showError("Missing consultationId in query parameters");
        return;
        }

        const paymentDetails = await getPaymentViaLinkDetails({ consultationID : consultationId });

        const {
        appointmentDate,
        email,
        slotStart,
        slotEnd,
        branchId,
        serviceId,
        companyId,
        amount
        } = paymentDetails;


        if (
          !appointmentDate || !email || !slotStart || !slotEnd ||
          !branchId || !serviceId || !companyId || !amount || !consultationId
        ) {
          showError("Missing required query parameters");
        }

        const payAmount = parseFloat(amount);

        const [companyDetails, companyServices, selectedBranch, consultationData] =
          await Promise.all([
            fetchCompanyDetails(companyId, serviceId),
            getCompanyServices(companyId),
            getBranchDetails(companyId, serviceId, branchId),
            fetchPatientConsultation(consultationId)
          ]);

        const patientId = consultationData?.patientId;

        setBookingState({
          bookingModel: { AppointmentDate: appointmentDate, Email: email },
          selectedServiceBookingSlot: { startTime: slotStart, endTime: slotEnd },
          selectedBranch,
          selectedPrivateService: serviceId,
          companyServices,
          payAmount,
          companyDetails,
          consultationId,
          patientId,
        });
      } catch (err) {
        console.error("Error loading booking data:", err);
        showError("Failed to load booking info.");
      }
    };

    loadBookingState();
  }, [searchParams]);

  
  useEffect(() => {
    if (!bookingState) return;

    const fetchClientSecret = async () => {
      try {
        const {
          selectedBranch,
          companyDetails,
          payAmount,
        } = bookingState;
        
        const ods = selectedBranch?.ods;
        const ryftAccountId = await getCompanyMarketAppAccountIdByCompanyId({
          companyID: companyDetails?.companyID,
        });
        setAccountId(ryftAccountId);

        const body = {
          returnUrl: "https://www.invatechhealth.com",
          currency: "GBP",
          ods,
          amount: Math.round(Number(payAmount) * 100),
        };

        const response = await createRyftSession({ data: body });
        const parsedData = JSON.parse(response);

        if (parsedData?.clientSecret) {
          setClientSecret(parsedData.clientSecret);
        } else {
          showError("Failed to get payment client secret");
        }
      } catch (err) {
        console.error("Failed to fetch client secret", err);
        showError("Payment setup failed.");
      }
    };

    fetchClientSecret();
  }, [bookingState]);

  
  useEffect(() => {
    if (!clientSecret || !window.Ryft) return;

    window.Ryft.init({
      publicKey: process.env.REACT_APP_PUBLIC_KEY,
      clientSecret,
      accountId,
      googlePay: {
        merchantIdentifier: "merchant_123",
        merchantName: "TitanVerse Limited",
        merchantCountryCode: "44",
      },
      applePay: {
        merchantName: "TitanVerse Limited",
        merchantCountryCode: "44",
      },
      style: {
        borderRadius: 10,
      },
    });

    const handleCardValidation = (e) => setIsCardValid(e.isValid);
    const handlePaymentResult = async (paymentSession) => {
      const session = paymentSession?.paymentSession;
      if (["Approved", "Captured"].includes(session?.status)) {
        try {
          const { bookingModel, selectedServiceBookingSlot, patientId, consultationId } = bookingState;
          const auditPayload = {
            Id: session.id,
            paymentSessionId: clientSecret,
            amount: session.amount,
            currency: session.currency,
            returnUrl: session.returnUrl,
            status: session.status,
            enabledPaymentMethods: session.enabledPaymentMethods,
            paymentMethod: {
              type: session.paymentMethod?.type,
              tokenizedDetails: {
                id: session?.paymentMethod?.tokenizedDetails?.id,
                stored: false,
              },
              card: {
                scheme: session.paymentMethod?.card?.scheme,
                last4: session.paymentMethod?.card?.last4,
              },
            },
            customerEmail: session.customerEmail,
            createdTimestamp: session.createdTimestamp,
            lastUpdatedTimestamp: session.lastUpdatedTimestamp,
            patientId: patientId,
            consultationId: consultationId
          };

          setPaymentSuccessful(true);
          await createPaymentAudit({ data: auditPayload });
          localStorage.clear();
        } catch (err) {
          console.error(err);
          showError("Something went wrong after payment.");
        }
      } else if (session?.lastError) {
        const userError = window.Ryft.getUserFacingErrorMessage(session.lastError);
        document.getElementById("ryft-pay-error").innerText = userError;
      }
    };

    window.Ryft.addEventHandler("cardValidationChanged", handleCardValidation);
    window.Ryft.addEventHandler("walletPaymentSessionResult", handlePaymentResult);

    return () => {
      
    };
  }, [clientSecret]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      const paymentSession = await window.Ryft.attemptPayment();
      if (["Approved", "Captured"].includes(paymentSession?.status)) {
        const { bookingModel, selectedServiceBookingSlot, patientId, consultationId } = bookingState;

        const auditPayload = {
          _id: paymentSession.id,
          paymentSessionId: clientSecret,
          amount: paymentSession.amount,
          currency: paymentSession.currency,
          returnUrl: paymentSession.returnUrl,
          status: paymentSession.status,
          enabledPaymentMethods: paymentSession.enabledPaymentMethods,
          paymentMethod: {
            type: paymentSession.paymentMethod?.type,
            tokenizedDetails: {
              id: paymentSession?.paymentMethod?.tokenizedDetails?.id,
              stored: false,
            },
            card: {
              scheme: paymentSession.paymentMethod?.card?.scheme,
              last4: paymentSession.paymentMethod?.card?.last4,
            },
          },
          customerEmail: paymentSession.customerEmail,
          createdTimestamp: paymentSession.createdTimestamp,
          lastUpdatedTimestamp: paymentSession.lastUpdatedTimestamp,
          patientId: patientId,
          consultationId: consultationId
        };

        setPaymentSuccessful(true);
        await createPaymentAudit({ data: auditPayload });
        setProcessing(false);
        localStorage.clear();
      } else if (paymentSession.lastError) {
        const userError = window.Ryft.getUserFacingErrorMessage(paymentSession.lastError);
        document.getElementById("ryft-pay-error").innerText = userError;
        setProcessing(false);
      }
    } catch (err) {
      document.getElementById("ryft-pay-error").innerText = "Payment failed. Try again.";
      setProcessing(false);
    }
  };

 if (!bookingState) return <p></p>;//<p>Loading appointment details...</p>;

  const {
    bookingModel,
    selectedServiceBookingSlot,
    selectedBranch,
    selectedPrivateService,
    companyServices,
    payAmount,
  } = bookingState;

  const matchedService = companyServices?.find(
    (svc) => svc._id === selectedPrivateService
  );

  return (
   <section
      className="booking-content-wrapper"
    
    >
      <section className="booking-content-container">
        <section className="left"></section>
        <section className="right">
        <section className="inner-content">
        <section className="step-content">
    {paymentSuccessful ? (
            <div className="bf-content-wrapper form-success-wrapper">
      <div className="form-success-parent">
        <div className="success-animation">
          <Lottie
            lottieRef={animRef}
            autoplay={false}
            animationData={successAnimation}
            loop={false}
          />
        </div>
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 100, delay: 2 }}
          exit={{ y: 50, opacity: 0 }}
          className="bf-content-body"
        >
          <div className="bf-content-header">
            <h2 className="bf-title" style={{ color: mainColor }}>
              You're all set.
            </h2>
            <p className="bf-subtitle">Payment successful.</p>
          </div>
            </motion.div>
            </div>
            </div>
            ) : (
        <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 100, delay: 0.5 }}
            exit={{ y: 50, opacity: 0 }}
            className="bf-content-wrapper payment-details-wrapper"
        >
            <div className="bf-content-body">
            <div className="bf-content-header">
                <h2 className="bf-title" style={{ color: mainColor }}>
                Confirm Your Appointment Details
                </h2>
            </div>

            <div className="content">
                <div className="details-box appointment-details">
                <div className="title-address">
                    <div className="title-box">
                    <p className="title consultation-title">
                        {matchedService?.serviceName}
                    </p>
                    <p className="consultation-date-time">
                        {bookingModel?.AppointmentDate && (
                        <span className="date">
                            {new Date(
                            bookingModel?.AppointmentDate
                            ).toLocaleDateString("en-GB", {
                            weekday: "short",
                            day: "2-digit",
                            month: "long",
                            })}
                        </span>
                        )}
                        <span className="time">
                        {selectedServiceBookingSlot?.startTime} -{" "}
                        {selectedServiceBookingSlot?.endTime} (1h)
                        </span>
                    </p>
                    </div>
                    <div className="address-box">
                    <PiMapPinLight size={20} />
                    <p>{selectedBranch?.pharmacyAddress}</p>
                    </div>
                </div>
                </div>

                <div className="details-box deposit-details">
                <p className="title deposit-title">
                    {matchedService?.serviceName} - Deposit
                </p>
                <div className="title-amount">
                    <p className="amount-title">{matchedService?.serviceName}</p>
                    <p className="amount">£{payAmount}</p>
                </div>
                <p className="note">
                    This deposit secures your slot and will be deducted from the total cost
                </p>
                <div className="total-box">
                    <p className="total-title">Total</p>
                    <p className="total-amount">£{payAmount}</p>
                </div>
                </div>
            
                <div className="details-box payment-details">
                <p className="title payment-title">Payment</p>
                <div className="payment-form">
                    <form
                    id="ryft-pay-form"
                    ref={formRef}
                    onSubmit={handleSubmit}
                    className="Ryft--payform"
                    >
                    {processing ? (
                        <div className="payment-processing-loader">
                        <CustomLoader width={45} height={45} />
                        </div>
                    ) : (
                        <button
                        id="pay-btn"
                        className={!isCardValid ? "pay-btn-disabled" : "pay-btn"}
                        disabled={!isCardValid}
                        type="submit"
                        >
                        Pay Now
                        </button>
                    )}

                    <div
                        id="ryft-pay-error"
                        style={{ color: "red", marginTop: "10px" }}
                    ></div>
                    </form>
                </div>
                </div>
            
                <div className="details-box cancellation-policy">
                <p className="title">Cancellation Policy</p>
                <p className="sub-text">
                    Free cancellations up to 48 hours before your appointment. After
                    that, deposits are non-refundable.
                </p>
                </div>
            </div>
            </div>
        </motion.div>
    )}
        </section>
        </section>

        </section>

        </section>
 
</section>
  );
}
